import type { ProductOutput } from "../product.types";
import type {
  OrderCodeOptionState,
  TopologyOrderCode,
} from "interfaces/product/product.types";
import { getAccessoryFromGroup } from "stores/product/product/productStore";

export const getTopologyConfigurationFromProduct = (
  seriesId: number,
  productOutput: ProductOutput,
) => {
  const currentStrategyId = productOutput?.product?.currentStrategyId;
  if (!currentStrategyId) {
    throw new Error(
      "Current strategy ID is required to convert to topology node",
    );
  }

  const { orderCode, selectedAccessories } = productOutput?.product || {};
  if (!orderCode) {
    throw new Error("Order code is required to convert to topology node");
  }

  const presentation = orderCode.presentation;
  if (!presentation) {
    throw new Error("Presentation is required to convert to topology node");
  }

  const pimId = productOutput.productImageList?.productImages?.find(
    (img) => img?.typ === "ProductPhoto",
  )?.pimId;

  const optionStates: OrderCodeOptionState[] = [];
  for (const part of orderCode.parts ?? []) {
    for (const option of part?.options ?? []) {
      for (const signature of option?.signatures ?? []) {
        optionStates.push({
          partKey: part?.key ?? "",
          optionKey: option?.key ?? "",
          signatureKey: signature?.key ?? "",
          signatureValue: signature?.value ?? "",
          signaturePresentation: signature?.presentation ?? "",
          separator: option?.separator ?? "",
          isSizePart: checkIsSizePart(part?.presentation ?? "") ?? false,
        });
      }
    }
  }

  const orderCodeMapped: TopologyOrderCode = {
    state: {
      optionStates,
      lastChange: null,
    },
    propertyStates: [],
    currentStrategyId: currentStrategyId,
    seriesId,
  };

  const selectedAccessoriesMapped = selectedAccessories?.map((accessory) => {
    if (!accessory?.accessoryGroupId) {
      throw new Error(
        "Accessory group ID is required to convert to topology node",
      );
    }
    if (!accessory?.currentPresentation) {
      throw new Error(
        "Accessory current presentation is required to convert to topology node",
      );
    }
    const accessoryFromGroup = getAccessoryFromGroup(
      accessory.currentPresentation,
      accessory.accessoryGroupId,
    );
    return {
      ...accessory,
      title: accessoryFromGroup?.serieAlias ?? "",
    };
  });

  return {
    orderCode: orderCodeMapped,
    presentation,
    pimId,
    selectedAccessories: selectedAccessoriesMapped ?? undefined,
  };
};

const checkIsSizePart = (partPresentation: string) => {
  const presentationParts = partPresentation.split("x");
  for (const part of presentationParts) {
    if (isNaN(Number(part.trim()))) {
      return false;
    }
  }
  return true;
};

export type TopologyConfiguration = ReturnType<
  typeof getTopologyConfigurationFromProduct
>;
